import addGetCurrentLine from "./execute_get_current_line.js"
import stringUtils from "./string_utils.js"
import errorModifier from "./error_modifier.js"

// cap at 100: display stops at the _toxilib_execute_for wrapper frame, and recursion
// ("Maximum call stack size exceeded") keeps its top frames — the ones actually shown —
// while Infinity would capture ~10k frames for error_modifier to regex-process (visible freeze)
Error.stackTraceLimit = 100

let scopeIdentifier = "__scope__"

let eventBus


function execute ({
    name = "anonymous_evaluator",
    source,
    scope = {},
    alias = {},
    thisValue,
    errorHandler,
    findOriginalPosition,
    syntaxErrorLocator,
    receiveInContextEvaluator,
    receiveGetCurrentLine,
    inContextEvaluatorErrorHandler,
    autoReturn,
    useAsyncFunction
}) {

    scope = clone(scope)

    let {
        runner,
        linesAddedToScope,
        generatedSource,
        error: runnerInstantiationError
    } = createEvaluator({
        name,
        source,
        scope,
        alias,
        receiveInContextEvaluator,
        receiveGetCurrentLine,
        inContextEvaluatorErrorHandler,
        autoReturn,
        useAsyncFunction
    })

    if (runnerInstantiationError) {
        runnerInstantiationError.sourceName = name
        locateInstantiationError(runnerInstantiationError, {
            generatedSource,
            linesAddedToScope,
            findOriginalPosition,
            syntaxErrorLocator,
            scope,
            sourceName: name
        })
        if (errorHandler) {
            errorHandler(runnerInstantiationError)
            return null
        } else {
            throw runnerInstantiationError
        }
    }


    const errorModifierParams = getErrorModifierParams({
        sourceName: name,
        autoReturn,
        useAsyncFunction,
        linesAddedToScope,
        findOriginalPosition
    })

    const errorHandlerParams = {name, errorHandler, errorModifierParams, useAsyncFunction}


    if (eventBus) {
        eventBus.emit("new errorModifier params", errorModifierParams)
    }

    if (useAsyncFunction) {
        return runner.call(thisValue, scope).catch(error => handleError(error, errorHandlerParams))
    } else {
        try {
            return runner.call(thisValue, scope)
        } catch (error) {
            return handleError(error, errorHandlerParams)
        }
    }

}


execute.attachEventBus = function (newEventBus) {
    eventBus = newEventBus
}



function handleError (error, {errorHandler, errorModifierParams, name = "", toxiTraceSuffix}) {

    error.toxiTrace = error.toxiTrace || []
    error.toxiTrace.unshift(`Toxilibs - execute.js ${name} - ${toxiTraceSuffix || "try catch runner.call"}`)

    if (!error.localStackLines) {
        errorModifier(error, errorModifierParams)
    }

    if (errorHandler) {
        errorHandler(error)
        return null
    } else {
        throw error
    }
}


// new Function() reports a compilation error with no position at all (V8 gives neither
// lineNumber nor a stack frame in the compiled source), so the terminal used to show such
// errors with a page name and no line. Re-parse the source we generated with the parser the
// caller provides: its position, shifted back by the lines we prepended, maps to a real page
// and line through findOriginalPosition, exactly like a runtime stack line.
function locateInstantiationError (error, params) {
    let position = positionInGeneratedSource(params)
    if (position) {
        position = originalPosition(position, params)
    }
    if (!position) {
        return
    }

    error.lineNumber       = position.line
    error.column           = position.column
    error.sourceName       = position.sourceName
    error.hasKnownPosition = true // keep it : no need for a fallback position anymore

    tagScopeRedeclaration(error, {message: position.message, scope: params.scope})
}


// where the parser puts the error, counted in the source we were handed : the lines we
// prepended for the scope come off the top
function positionInGeneratedSource ({generatedSource, linesAddedToScope = 0, syntaxErrorLocator}) {
    if (!syntaxErrorLocator || !generatedSource) {
        return null
    }

    let position = syntaxErrorLocator(generatedSource)
    if (!position || !position.line) {
        return null // the parser disagrees with the engine : no position beats a wrong one
    }

    let line = position.line - linesAddedToScope

    // line < 1 : the clash sits in the scope preamble, not in any user source
    return (line < 1) ? null : {line, column: position.column, message: position.message}
}


function originalPosition (position, {findOriginalPosition, sourceName}) {
    if (!findOriginalPosition) {
        return Object.assign({sourceName}, position)
    }

    let original = findOriginalPosition(position)
    if (!original || !original.line) {
        return null
    }

    return {
        line:       original.line,
        column:     original.column,
        sourceName: original.sourceName || sourceName,
        message:    position.message
    }
}


const redeclarationRegex = /Identifier '([^']+)' has already been declared/


// a name declared in the source can collide with a variable we inject in the scope. The
// engine then reports a redeclaration whose first declaration is nowhere in the user's
// code : tag it so the host can explain what really happened.
function tagScopeRedeclaration (error, {message = "", scope = {}}) {
    let match = String(message).match(redeclarationRegex)
    if (match && Object.prototype.hasOwnProperty.call(scope, match[1])) {
        error.redeclaredScopeVariable = match[1]
    }
}


function getErrorModifierParams ({
    simpleMode, //FIXME remove
    sourceName,
    autoReturn, useAsyncFunction,
    findOriginalPosition, linesAddedToScope = 0
}) {

    sourceName = encodeURIComponent(sourceName)

    let params = {
        sourceName,
        findOriginalPosition
    }

    if (autoReturn) {
        params.from = "eval"
        params.ignoredLines = 0
    } else {
        params.from = useAsyncFunction ? "asyncFunctionEval" : "functionEval"
        params.ignoredLines = linesAddedToScope
    }

    if (simpleMode) {
        return params
    } else {
        let paramsBySource = {}
        paramsBySource[sourceName] = params
        return {paramsBySource}
    }
}


const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor


function createEvaluator ({ //eslint-disable-line complexity
    name,
    source,
    scope,
    alias,
    receiveInContextEvaluator,
    receiveGetCurrentLine,
    inContextEvaluatorErrorHandler,
    autoReturn,
    useAsyncFunction
}) {

    let suffix = ""

    let sourceName = name

    name = `_toxilib_execute_for_${stringUtils.camelize(name)}`
    name = name.replace(/[^A-Za-z0-9_$]/g, "_") // ensure a valid JS identifier for the stack-trace function name

    if (receiveInContextEvaluator) {
        suffix += addEvaluator({scope, receiveInContextEvaluator, inContextEvaluatorErrorHandler})
    }

    if (autoReturn) {
        let sourceVarName = `_toxilib_execute_mySource${varSuffixId}`
        scope[sourceVarName] = source
        source = `return eval(${sourceVarName})`
        varSuffixId++
    }

    let errorModifierParams = getErrorModifierParams({
        simpleMode: true, //FIXME remove
        sourceName: name,
        autoReturn,
        useAsyncFunction
    })

    if (receiveGetCurrentLine) { //FIXME remove
        addGetCurrentLine({name, source, errorModifierParams, receiveGetCurrentLine})
    }


    let {source: prefix, linesAddedToScope} = variablesDefinitionSource(scope, alias)


    //this is used to have a nice function name in the stack trace
    if (useAsyncFunction) {
        prefix = `async function ${name} () {\n ${prefix}`
        suffix += `\n}\n return await ${name}.call(this)`
    } else {
        prefix = `function ${name} () { \n${prefix}`
        suffix += `\n}\n return ${name}.call(this)`
    }

    suffix += `\n//# sourceURL=${encodeURIComponent(sourceName)}`
    prefix = `"use strict";\n${prefix}`

    linesAddedToScope += 2

    errorModifierParams.ignoredLines = linesAddedToScope


    const FunctionConstructor = (useAsyncFunction ? AsyncFunction : Function)

    const generatedSource = prefix + source + suffix

    let runner
    try {
        runner = new FunctionConstructor(scopeIdentifier, generatedSource)
    } catch (error) {
        error.toxiTrace = error.toxiTrace || []
        error.toxiTrace.unshift("Toxilibs - execute.js - try catch evaluator instantiation")
        return {error, generatedSource, linesAddedToScope}
    }

    return {
        runner,
        linesAddedToScope
    }
}





function addEvaluator ({scope, receiveInContextEvaluator, inContextEvaluatorErrorHandler}) {
    scope.__errorModifier             = errorModifier
    scope.__errorHandler              = inContextEvaluatorErrorHandler
    scope.__receiveInContextEvaluator = receiveInContextEvaluator
    scope.__variableDefinitionSource  = variablesDefinitionSource
    return `\n__receiveInContextEvaluator(${inContextEvaluatorSource()})`
}


/*eslint-disable*/
let inContextEvaluatorCode = function inContextEvaluator (sourceVarName, scopeVarName) {
    preEval
    let evalResult
    try {
        evalResult = eval(sourceVarName)
        //__getLinesAddedToScope(variableDefinitionResult.linesAddedToScope //FIXME idea...
    } catch (error) {

        error.toxiTrace = error.toxiTrace || []
        error.toxiTrace.unshift('Toxilibs - execute.js - inContextEvaluator - try catch eval')

        __errorModifier(error, {
            from: 'eval',
            ignoredLines: variableDefinitionResult.linesAddedToScope
        })
        //__getLinesAddedToScope(variableDefinitionResult.linesAddedToScope //FIXME idea...
        if (__errorHandler) {
            __errorHandler(error)
        } else {
            throw error
        }
    }
    return evalResult
}.toString()
/*eslint-enable*/



let varSuffixId = 0
function inContextEvaluatorSource () {
    let varNameSuffix = `_toxilib_execute${varSuffixId++}`
    let sourceVarName = `source${varNameSuffix}`
    let scopeVarName  = `scope${varNameSuffix}`
    let preEval       = addScopeToSourceInsideEval(sourceVarName, scopeVarName)

    let source = inContextEvaluatorCode

    source = source.replace(/preEval/g,       preEval)
    source = source.replace(/sourceVarName/g, sourceVarName)
    source = source.replace(/scopeVarName/g,  scopeVarName)

    return source
}



function addScopeToSourceInsideEval (sourceVarName, scopeVarName) {
    return `let variableDefinitionResult = __variableDefinitionSource(${scopeVarName}, {}, "${scopeVarName}")\n${//FIXME bad name 'variableDefinitionResult'
        sourceVarName} = variableDefinitionResult.source + ${sourceVarName}\n`
}


/**
 * example of alias param:
 *  {
 *     a: ['aa', 'aaa'],
 *     b: 'bb'
 *  }
 * Result string would be:
 * let a   = __scope__.a
 * let aa  = __scope__.a
 * let aaa = __scope__.a
 * let b   = __scope__.b
 * let bb  = __scope__.b
 */
function variablesDefinitionSource (scope, alias, specificScopeIdentifier = scopeIdentifier) {

    let linesAddedToScope = 0
    let variablesDefinitions = ""

    function addVariableDefinition (name, varName) {
        variablesDefinitions += `let ${name} = ${specificScopeIdentifier}.${varName}\n`
        linesAddedToScope++
    }

    for (let varName in scope) {
        let namesForThisVar = namesForVar(alias, varName)
        namesForThisVar.forEach(name => addVariableDefinition(name, varName))
    }

    return {
        source: variablesDefinitions,
        linesAddedToScope
    }
}


function namesForVar (alias, varName) {
    return (alias[varName] ? [varName].concat(alias[varName]) : [varName])
}



function clone (object) {
    const newObject = {}

    Object.getOwnPropertyNames(object).forEach((key) => {
        newObject[key] = object[key]
    })

    return newObject
}


export default execute

